import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { Typography, Table, Button, Tag, Flex, App, Modal, Form, Input, theme } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useState } from "react";
import { useQ_PageGodMode_Organizations, type PageGodMode_Organizations_QueryData } from "@/hooks/GodMode/useQ_PageGodMode_Organizations";
import { useM_PageGodMode_OrganizationCreate } from "@/hooks/GodMode/useM_PageGodMode_OrganizationCreate";
import { const_OrganizationsStatusOptions } from "@/hooks/GodMode/const_OrganizationsStatusOptions";
import type { Supabase_Enums } from "@gotoIHUB/db-types";

export const Route = createFileRoute("/_protected/_god-mode/god-mode/")({
  component: Page_GodMode,
});

function Page_GodMode() {
  const { token } = theme.useToken();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const qOrganizations = useQ_PageGodMode_Organizations();
  const mOrganizationCreate = useM_PageGodMode_OrganizationCreate();
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createForm] = Form.useForm();

  const handleCreate = async (values: { name: string }) => {
    try {
      await mOrganizationCreate.mutation.mutateAsync(values);
      message.success("Organization created");
      setCreateModalOpen(false);
      createForm.resetFields();
    } catch {
      message.error("Failed to create organization");
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: Supabase_Enums<"organization_status">) => {
        const meta = const_OrganizationsStatusOptions.map[status];
        return meta ? <Tag color={meta.color}>{meta.label}</Tag> : null;
      },
    },
    {
      title: "Members",
      key: "members",
      render: (_: unknown, record: PageGodMode_Organizations_QueryData[number]) =>
        (record.organization_members as { count: number }[])?.[0]?.count ?? 0,
    },
    {
      title: "Created",
      dataIndex: "created_at",
      key: "created_at",
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
  ];

  return (
    <div>
      <Flex gap={token.marginMD} style={{ marginBottom: token.marginLG }}>
        <Button type="primary">Organizations</Button>
        <Link to="/god-mode/flows">
          <Button type={undefined}>Flows</Button>
        </Link>
      </Flex>

      <Flex justify="space-between" align="center" style={{ marginBottom: token.marginLG }}>
        <Typography.Title level={2} style={{ margin: 0 }}>Organizations</Typography.Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateModalOpen(true)}>
          Create Organization
        </Button>
      </Flex>

      <Table
        dataSource={qOrganizations.organizations}
        columns={columns}
        rowKey="id"
        loading={qOrganizations.query.isLoading}
        pagination={false}
        onRow={(record) => ({
          onClick: () => navigate({ to: "/god-mode/$orgId", params: { orgId: record.id } }),
          style: { cursor: "pointer" },
        })}
      />

      <Modal
        title="Create Organization"
        open={createModalOpen}
        onCancel={() => { setCreateModalOpen(false); createForm.resetFields(); }}
        onOk={() => createForm.submit()}
        confirmLoading={mOrganizationCreate.mutation.isPending}
        destroyOnHidden
      >
        <Form form={createForm} layout="vertical" onFinish={handleCreate}>
          <Form.Item name="name" label="Name" rules={[{ required: true, message: "Name is required" }]}>
            <Input placeholder="Organization name" autoFocus />
          </Form.Item>
        </Form>
      </Modal>

    </div>
  );
}
